import { z } from 'zod';
import type { ApolloError } from '@apollo/client';
import { GRAPHQL_ERROR_CODES } from '@frameio/graphql-client/enums';

export type RateLimitInfo = {
  isRateLimited: true;
  remaining: number;
  attempted: number;
  // not currently in use, but probably used imminently
  limit?: number;
  resetsAt?: Date;
};

type GraphQLErrorLike = {
  extensions?: Record<string, unknown> | null;
};

/**
 * Accepts either a thrown `ApolloError` (default mutation error policy) or the
 * `errors` array returned on a mutation result (error policy `all`), so a single
 * parser covers every call site.
 */
export type RateLimitErrorInput =
  | ApolloError
  | readonly GraphQLErrorLike[]
  | null
  | undefined;

function getGraphQLErrors(
  input: RateLimitErrorInput
): readonly GraphQLErrorLike[] {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  return (input as ApolloError).graphQLErrors ?? [];
}

const rateLimitValueSchema = z.object({
  generations_remaining: z.number(),
  generations_attempted: z.number(),
  generations_limit: z.number().optional().catch(undefined),
  resets_at: z.coerce.date().optional().catch(undefined),
});

/**
 * Reads structured rate-limit fields from a GraphQL error's `extensions`. Returns
 * `null` for anything that isn't a well-formed `TOO_MANY_REQUESTS` error.
 */
export function parseRateLimitError(
  input: RateLimitErrorInput
): RateLimitInfo | null {
  const rateLimitError = getGraphQLErrors(input).find(
    (error) =>
      (error?.extensions as { code?: unknown } | undefined)?.code ===
      GRAPHQL_ERROR_CODES.TOO_MANY_REQUESTS
  );

  const parsed = rateLimitValueSchema.safeParse(
    (rateLimitError?.extensions as { value?: unknown } | undefined)?.value
  );
  if (!parsed.success) return null;

  return {
    isRateLimited: true,
    remaining: parsed.data.generations_remaining,
    attempted: parsed.data.generations_attempted,
    limit: parsed.data.generations_limit,
    resetsAt: parsed.data.resets_at,
  };
}
